/**
 * XRGallery - Unified Viewer Entry Point
 * VR 360 Video/Image/Model Viewer built with Babylon.js
 *
 * This viewer works in two modes:
 * - HOSTED MODE: With Firebase credentials for analytics and Firestore config
 * - EXPORT MODE: Standalone with embedded config, no Firebase dependencies
 */

// Initialize Sentry as early as possible
import * as Sentry from "@sentry/browser";

Sentry.init({
  dsn: "https://98f444c73599f68edcf65d9db57c665f@o4510773732245504.ingest.us.sentry.io/4510774427189248",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
  _experiments: {
    enableLogs: true,
  },
  // Ignore errors from Sentry's own tracing in bots with incomplete Performance API
  ignoreErrors: [
    /Cannot read properties of undefined \(reading 'replaceAll'\)/,
  ],
  // Tracing
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.xrgallery\.io/],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Expose Sentry globally for debugging
if (typeof window !== 'undefined') {
  window.Sentry = Sentry;
}

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import '@babylonjs/core/Audio/audioSceneComponent'; // Register audio scene component
import { CreateAudioEngineAsync } from '@babylonjs/core/AudioV2/webAudio/webAudioEngine';
import type { AbstractSound } from '@babylonjs/core/AudioV2/abstractAudio/abstractSound';

// Infer AudioEngineV2 type from factory function return
type AudioEngineV2 = Awaited<ReturnType<typeof CreateAudioEngineAsync>>;
import { SceneManager } from './scenes/SceneManager';
import { UIManager } from './ui/UIManager';
import { XRHelper } from './utils/XRHelper';
import { LoadingUI } from './ui/LoadingUI';

// Service layer imports
import {
  createServices,
  type ViewerServices,
  type InitOptions,
  type IAnalyticsService,
  type IConfigProvider,
  type IUserProfileService,
  type CreateOptions,
  type ViewerInstance,
} from './services';

// Create API helpers
import { DEFAULT_FIREBASE_CONFIG } from './api/firebaseConfig';
import { setupCanvas } from './api/canvasManager';
import { resolveCreateOptions, waitForDOMReady } from './api/createOptions';

// Firebase auth for admin bypass
import { getAuth } from 'firebase/auth';

// Import inspector for debugging
import '@babylonjs/inspector';
import { Logger } from './utils/Logger';
import { initWebVitals } from './utils/webVitals';
import type { ExperienceConfig } from './types/config';

const logger = Logger.getLogger('XRGallery');

class WebXRGallery {
  private canvas: HTMLCanvasElement;
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private sceneManager: SceneManager;
  private uiManager: UIManager;
  private xrHelper: XRHelper;
  private loadingUI: LoadingUI;
  private audioEngine!: AudioEngineV2; // Initialized in init()
  private initializationPromise: Promise<void>;
  private initializationError: Error | null = null;

  // Injected services
  private analyticsService: IAnalyticsService;
  private configProvider: IConfigProvider;
  private userService: IUserProfileService;

  constructor(services: ViewerServices, canvas?: HTMLCanvasElement) {
    // Store injected services
    this.analyticsService = services.analyticsService;
    this.configProvider = services.configProvider;
    this.userService = services.userService;
    // Get canvas element — use provided canvas or fall back to DOM lookup
    this.canvas = canvas ?? document.getElementById('renderCanvas') as HTMLCanvasElement;
    if (!this.canvas) {
      throw new Error('Canvas element not found');
    }

    // Create Babylon engine (WITHOUT old audio engine)
    this.engine = new Engine(this.canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      xrCompatible: true,
      audioEngine: false, // Disable old audio engine - we'll use V2
    });

    // Create scene
    this.scene = new Scene(this.engine);
    this.scene.clearColor.set(0, 0, 0, 1); // Black background

    // Create camera FIRST (needed for audio listener)
    this.camera = new ArcRotateCamera(
      'camera',
      0,
      Math.PI / 2,
      0,
      Vector3.Zero(),
      this.scene
    );
    this.camera.attachControl(this.canvas, true);
    this.camera.lowerRadiusLimit = 0;
    this.camera.upperRadiusLimit = 0;
    this.camera.wheelPrecision = 100;

    // Invert camera drag direction (both horizontal and vertical)
    // This makes dragging feel like "grabbing" the scene rather than moving the camera
    // Reduce sensitivity (multiply by higher value = slower movement)
    this.camera.angularSensibilityX *= -1.875;
    this.camera.angularSensibilityY *= -1.875;

    // Setup FOV zoom controls
    this.setupFOVZoom();

    // Track session start (analytics service is already injected)
    this.analyticsService.trackSessionStarted();

    // Initialize LoadingUI
    this.loadingUI = new LoadingUI();

    // Initialize managers
    this.sceneManager = new SceneManager(this.scene);
    this.uiManager = new UIManager(this.scene, this.sceneManager, this.loadingUI); // Pass loadingUI to UIManager
    this.xrHelper = new XRHelper(this.scene);

    // Inject analytics service into managers
    this.sceneManager.analyticsService = this.analyticsService;
    this.uiManager.analyticsService = this.analyticsService;
    this.xrHelper.analyticsService = this.analyticsService;

    // Connect XRHelper to UIManager for VR state handling
    this.uiManager.setXRHelper(this.xrHelper);

    // Inject analytics into DomeComponent (via SceneManager)
    const domeComponent = this.sceneManager.getDomeComponent();
    if (domeComponent) {
      domeComponent.analyticsService = this.analyticsService;
    }

    // Setup
    this.initializationPromise = this.initialize();
  }

  /**
   * Initialize the application
   */
  private async initialize(): Promise<void> {
    try {
      logger.info('Initializing XRGallery...');

      // Show loading screen
      this.loadingUI.show(true);
      this.loadingUI.updateProgress(0, 'Initializing engine...');

      // Initialize AudioEngineV2 and attach listener to camera
      await this.initAudioEngine();

      // Create UI
      this.uiManager.createUI();

      // Initialize WebXR
      this.loadingUI.updateProgress(2, 'Setting up WebXR...');
      await this.setupWebXR();

      // Setup VR controller button mapping
      this.uiManager.setupControllerMapping(this.xrHelper);

      // Load initial media (from URL or default config)
      this.loadingUI.updateProgress(4, 'Loading configuration...');
      await this.loadConfigFromURL();

      // Smart Progressive Loading: We only load the first stage initially
      // Linked stages will be background-loaded automatically by SceneManager
      
      // Initialize UI with loaded config
      // Map stage loading to 10-100% of the loading bar
      await this.uiManager.initializeWithLoadedConfig(10, 90);


      // Start render loop
      this.startRenderLoop();

      // Setup window resize handler
      this.setupWindowResize();

      // Setup inspector keyboard control
      this.setupInspectorControl();

      // Add AudioEngineV2 debug helper since sounds don't show in Inspector
      this.setupAudioV2Debug();

      logger.info('XRGallery initialized successfully!');

    } catch (error) {
      const initError = error instanceof Error ? error : new Error(String(error));
      this.initializationError = initError;

      logger.error('Failed to initialize XRGallery:', initError);
      Sentry.captureException(initError);
      this.analyticsService.trackError(
        'initialization_error',
        initError.message,
        initError.stack
      );
      this.showError('Failed to initialize. Please check console for details.');
    }
  }

  /**
   * Load configuration using the injected config provider
   * Works in both hosted mode (Firestore) and export mode (embedded config)
   */
  private async loadConfigFromURL(): Promise<void> {
    try {
      this.loadingUI.updateProgress(5, 'Loading configuration...');

      // Check if password is required (only in hosted mode)
      // Admin users can bypass password protection
      if (await this.configProvider.requiresPassword()) {
        const sequenceId = this.configProvider.getSequenceId();
        if (sequenceId) {
          // Check if current user is admin (can bypass password)
          let isAdmin = false;
          try {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (currentUser) {
              const userProfile = await this.userService.getUserProfile(currentUser.uid);
              if (userProfile?.tier === 'admin') {
                isAdmin = true;
                logger.debug('Admin user detected - bypassing password protection');
              }
            }
          } catch (e) {
            // Auth not available or error - continue with normal password flow
            logger.debug('Could not check admin status:', e);
          }

          if (!isAdmin) {
            await this.showPasswordModal(sequenceId);
          }
        }
      }

      this.loadingUI.updateProgress(6, 'Fetching sequence...');

      // Load config using the provider
      const { config, metadata } = await this.configProvider.loadConfig();

      logger.info('Configuration loaded');
      this.loadingUI.updateProgress(8, 'Processing sequence data...');

      // Handle metadata if available (hosted mode with Firestore)
      if (metadata) {
        // Set analytics context
        this.analyticsService.setContext(metadata.sequenceId, metadata.userId);

        // Check if analytics is enabled at both user and sequence levels
        let analyticsEnabled = metadata.analyticsEnabled;

        // Check user-level analytics setting (account owner)
        try {
          const userProfile = await this.userService.getUserProfile(metadata.userId);
          if (userProfile && userProfile.analyticsEnabled === false) {
            analyticsEnabled = false;
            logger.debug('Analytics disabled at user level for:', metadata.userId);
          }

          // Handle branding/logo on loading screen
          if (metadata.logoUrl && userProfile && this.userService.hasFeatureAccess(userProfile, 'white_label')) {
            this.loadingUI.setLogo(metadata.logoUrl);
            logger.debug('Custom logo set:', metadata.logoUrl);
          } else if (userProfile && this.userService.hasFeatureAccess(userProfile, 'white_label') && metadata.removeBranding) {
            this.loadingUI.hideBranding();
            logger.debug('White-label mode: no branding');
          }

          // Handle "Powered by XRGallery" footer
          if (userProfile && this.userService.hasFeatureAccess(userProfile, 'white_label') && metadata.removeFooterBranding) {
            const footerEl = document.querySelector('.powered-by[data-branding="webxr-gallery"]');
            if (footerEl) {
              (footerEl as HTMLElement).style.display = 'none';
              logger.debug('Footer branding hidden');
            }
          }
        } catch (error) {
          logger.warn('Could not check user profile, using sequence settings:', error);
        }

        this.analyticsService.setEnabled(analyticsEnabled);

        this.analyticsService.trackSequenceLoaded(
          metadata.name,
          metadata.isPublic,
          metadata.hasPassword,
          metadata.orgId
        );
        this.analyticsService.trackSequenceViewStarted();
      }

      // Load the config into UIManager
      try {
        this.uiManager.loadConfigFromObject(config);

        // Apply viewer settings from metadata
        if (metadata?.disableTransitions) {
          this.uiManager.setDisableTransitions(true);
          logger.debug('Transitions disabled for this sequence');
        }
        if (metadata?.disableGazeNavigation) {
          this.uiManager.setDisableGazeNavigation(true);
          logger.debug('Gaze navigation disabled for this sequence');
        }

        // Apply default FOV from config (if set)
        if (config.experience?.defaultFOV) {
          this.camera.fov = config.experience.defaultFOV;
          logger.debug('Applied custom default FOV:', config.experience.defaultFOV, 'radians');
        }

        // Initialize global background audio (plays across all scenes)
        if (config.globalAudio?.url) {
          logger.debug('Initializing global background audio');
          await this.sceneManager.initializeGlobalAudio(config.globalAudio);
        }

        // Initialize lead capture forms (if metadata available)
        if (metadata?.sequenceId) {
          const sessionId = this.analyticsService.getSessionId();
          this.uiManager.initializeLeadCaptureForms(metadata.sequenceId, sessionId);
        }
      } catch (configError) {
        logger.error('Error loading sequence configuration:', configError);
        Sentry.captureException(configError);
        this.analyticsService.trackError(
          'config_load_error',
          configError instanceof Error ? configError.message : String(configError),
          configError instanceof Error ? configError.stack : undefined
        );
        throw configError;
      }

    } catch (error) {
      logger.error('Error loading config:', error);
      const appError = error as { code?: string; message?: string };

      // Check if error is due to subscription inactive (lapsed payment)
      if (appError?.code === 'subscription-inactive') {
        logger.warn('Tour unavailable - owner subscription inactive');
        this.showSubscriptionInactiveModal();
      // Check if error is due to bandwidth limit exceeded (hard limit)
      } else if (appError?.code === 'bandwidth-exceeded') {
        logger.warn('Tour unavailable - bandwidth limit exceeded');
        this.showBandwidthExceededModal();
      // Check if error is due to permission denied (private sequence)
      } else if (appError?.code === 'permission-denied' || appError?.message?.includes('permission')) {
        logger.warn('Access denied to private sequence');
        this.showAccessDeniedModal();
      } else if (appError?.message?.includes('not found') || appError?.message?.includes('No sequence ID')) {
        logger.warn('Sequence not found, redirecting to home');
        window.location.href = '/';
      } else {
        // For export mode, don't redirect - show error
        this.showError('Failed to load configuration: ' + (appError?.message ?? 'Unknown error'));
      }
    }
  }

  /**
   * Show password modal and wait for correct password
   */
  private async showPasswordModal(_sequenceId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const modal = document.getElementById('passwordModal');
      const form = document.getElementById('passwordForm') as HTMLFormElement;
      const input = document.getElementById('passwordInput') as HTMLInputElement;
      const error = document.getElementById('passwordError');

      if (!modal || !form || !input || !error) {
        logger.error('Password modal elements not found');
        reject(new Error('Password modal not found'));
        return;
      }

      // Show modal
      modal.classList.add('show');
      input.focus();

      // Handle form submission
      const handleSubmit = async (e: Event) => {
        e.preventDefault();
        const password = input.value;

        // Verify password using the config provider
        const isValid = await this.configProvider.verifyPassword(password);

        if (isValid) {
          // Hide modal
          modal.classList.remove('show');
          form.removeEventListener('submit', handleSubmit);
          resolve();
        } else {
          // Show error
          error.style.display = 'block';
          input.value = '';
          input.focus();
        }
      };

      form.addEventListener('submit', handleSubmit);
    });
  }

  /**
   * Show access denied modal for private sequences
   */
  private showAccessDeniedModal(): void {
    const modal = document.getElementById('accessDeniedModal');

    if (!modal) {
      logger.error('Access denied modal not found');
      return;
    }

    // Show modal
    modal.classList.add('show');
  }

  /**
   * Show subscription inactive modal when tour owner's subscription has lapsed
   */
  private showSubscriptionInactiveModal(): void {
    const modal = document.getElementById('subscriptionInactiveModal');

    if (!modal) {
      logger.error('Subscription inactive modal not found');
      return;
    }

    // Hide loading screen first
    this.loadingUI.hide();

    // Show modal
    modal.classList.add('show');
  }

  /**
   * Show bandwidth exceeded modal for hard limit users
   */
  private showBandwidthExceededModal(): void {
    const modal = document.getElementById('bandwidthExceededModal');

    if (!modal) {
      logger.error('Bandwidth exceeded modal not found');
      return;
    }

    // Hide loading screen first
    this.loadingUI.hide();

    // Show modal
    modal.classList.add('show');
  }

  /**
   * Initialize AudioEngineV2 and attach listener to camera
   */
  private async initAudioEngine(): Promise<void> {
    try {
      logger.debug('Creating AudioEngineV2...');

      // Create the new audio engine (resumeOnInteraction defaults to true - auto unlocks on click)
      this.audioEngine = await CreateAudioEngineAsync();

      // CRITICAL: Attach listener to camera for spatial audio to work
      this.audioEngine.listener.attach(this.camera);

      logger.debug('AudioEngineV2 created and listener attached to camera');

      // Hide Babylon's default unmute button - we use our own custom UI
      this.hideBabylonUnmuteButton();

      // Pass audio engine to SceneManager/HotspotComponent
      this.sceneManager.setAudioEngine(this.audioEngine);

      // Setup AudioEngineV2 unlock on user interaction
      this.setupAudioEngineV2Unlock();

    } catch (error) {
      logger.error('Failed to create AudioEngineV2:', error);
      Sentry.captureException(error);
      // Don't throw - app can still work without audio
    }
  }

  /**
   * Setup AudioEngineV2 unlock on first user interaction
   */
  private setupAudioEngineV2Unlock(): void {
    const unlockEvents = ['click', 'touchstart', 'keydown', 'pointerup'];

    const unlockHandler = async () => {
      if (!this.audioEngine) return;

      try {
        logger.debug('Unlocking AudioEngineV2, state:', this.audioEngine.state);

        await this.audioEngine.unlockAsync();
        logger.debug('AudioEngineV2 unlocked');

        // Resume the engine (may be needed separately from unlock)
        try {
          await this.audioEngine.resumeAsync();
          logger.debug('AudioEngineV2 resumed, state:', this.audioEngine.state);
        } catch (resumeError) {
          logger.warn('AudioEngineV2 resumeAsync failed (may recover on next interaction):', resumeError);
        }

        // Remove all listeners after successful unlock
        unlockEvents.forEach(event => {
          document.removeEventListener(event, unlockHandler);
        });
      } catch (error) {
        logger.error('Error unlocking AudioEngineV2:', error);
      }
    };

    // Add listeners to document
    unlockEvents.forEach(event => {
      document.addEventListener(event, unlockHandler);
    });
  }

  /**
   * Hide Babylon's default unmute button (babylonUnmute class)
   * We use our own custom UI for audio unlock prompts
   */
  private hideBabylonUnmuteButton(): void {
    // Add CSS to hide the default Babylon unmute button
    const style = document.createElement('style');
    style.id = 'hide-babylon-unmute';
    style.textContent = `
      .babylonUnmuteIcon,
      .babylonUnmute,
      [class*="babylonUnmute"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    // Also try to disable the legacy audio engine's button if it exists
    // Engine has an optional audioEngine property for legacy audio
    const engineWithAudio = this.engine as Engine & { audioEngine?: { useCustomUnlockedButton?: boolean } };
    if (engineWithAudio.audioEngine && 'useCustomUnlockedButton' in engineWithAudio.audioEngine) {
      engineWithAudio.audioEngine.useCustomUnlockedButton = true;
    }
  }

  /**
   * Setup FOV zoom controls (scroll wheel on desktop, pinch on mobile)
   */
  private setupFOVZoom(): void {
    // FOV zoom configuration
    const MIN_FOV = 0.3;  // Zoomed in (narrow field of view)
    const MAX_FOV = 2.5;  // Zoomed out (ultra-wide field of view)
    const DEFAULT_FOV = 1.5;  // Default field of view (wider starting view)

    // Set initial FOV
    this.camera.fov = DEFAULT_FOV;

    // Desktop: Mouse wheel zoom
    this.canvas.addEventListener('wheel', (event: WheelEvent) => {
      event.preventDefault();

      const delta = Math.sign(event.deltaY);
      const zoomSpeed = 0.01875; // Reduced to 37.5% of original
      const previousFOV = this.camera.fov;

      // Update FOV with clamping
      const newFOV = Math.max(MIN_FOV, Math.min(MAX_FOV,
        this.camera.fov + delta * zoomSpeed
      ));
      this.camera.fov = newFOV;

      // Track FOV zoom
      if (newFOV !== previousFOV) {
        this.analyticsService.trackFOVZoom(newFOV, previousFOV, 'wheel');
      }
    }, { passive: false });

    // Mobile: Pinch zoom
    let lastDistance = 0;
    let isPinching = false;

    this.canvas.addEventListener('touchstart', (event: TouchEvent) => {
      if (event.touches.length === 2) {
        isPinching = true;
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        lastDistance = this.getTouchDistance(touch1, touch2);
      }
    });

    this.canvas.addEventListener('touchmove', (event: TouchEvent) => {
      if (isPinching && event.touches.length === 2) {
        event.preventDefault();

        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = this.getTouchDistance(touch1, touch2);

        if (lastDistance > 0) {
          // Calculate zoom delta based on pinch distance change
          const delta = lastDistance - currentDistance;
          const zoomSpeed = 0.001875; // Reduced to 37.5% of original
          const previousFOV = this.camera.fov;

          // Update FOV with clamping
          const newFOV = Math.max(MIN_FOV, Math.min(MAX_FOV,
            this.camera.fov + delta * zoomSpeed
          ));
          this.camera.fov = newFOV;

          // Track FOV zoom (debounced via batch system)
          if (newFOV !== previousFOV) {
            this.analyticsService.trackFOVZoom(newFOV, previousFOV, 'pinch');
          }
        }

        lastDistance = currentDistance;
      }
    }, { passive: false });

    this.canvas.addEventListener('touchend', (event: TouchEvent) => {
      if (event.touches.length < 2) {
        isPinching = false;
        lastDistance = 0;
      }
    });
  }

  /**
   * Calculate distance between two touch points
   */
  private getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Setup WebXR
   */
  private async setupWebXR(): Promise<void> {
    // Check if WebXR is supported
    const isSupported = await XRHelper.isXRSupported();

    if (isSupported) {
      logger.info('WebXR is supported');

      try {
        // Initialize XR
        await this.xrHelper.initializeXR();

        // Show Enter VR button
        this.setupEnterVRButton();
        
        // Update UI layout to account for VR button
        this.uiManager.updateLayout();

      } catch (error) {
        logger.warn('WebXR initialization failed:', error);
        Sentry.captureException(error);
      }

    } else {
      logger.warn('WebXR is not supported on this device/browser');
    }
  }

  /**
   * Setup Enter VR button
   */
  private setupEnterVRButton(): void {
    const enterVRBtn = document.getElementById('enterVR') as HTMLButtonElement;
    if (!enterVRBtn) return;

    enterVRBtn.style.display = 'block';
    enterVRBtn.disabled = false;

    enterVRBtn.addEventListener('click', async () => {
      try {
        await this.xrHelper.enterVR();
      } catch (error) {
        logger.error('Failed to enter VR:', error);
        Sentry.captureException(error);
        alert('Failed to enter VR mode. Please check your VR headset connection.');
      }
    });
  }

  /**
   * Start the render loop
   */
  private startRenderLoop(): void {
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  /**
   * Setup window resize handler
   */
  private setupWindowResize(): void {
    window.addEventListener('resize', () => {
      this.engine.resize();
    });
  }

  /**
   * Setup inspector keyboard control (Ctrl+Shift+I or Cmd+Shift+I)
   */
  private setupInspectorControl(): void {
    window.addEventListener('keydown', (event) => {
      // Ctrl+Shift+I (Windows/Linux) or Cmd+Shift+I (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'I') {
        event.preventDefault();

        if (this.scene.debugLayer.isVisible()) {
          this.scene.debugLayer.hide();
          logger.debug('Inspector hidden');
        } else {
          this.scene.debugLayer.show({
            embedMode: false,
            overlay: true,
            globalRoot: document.body,
          });
          logger.debug('Inspector shown');
        }
      }
    });
  }

  /**
   * Setup AudioEngineV2 debug helper (since sounds don't show in Inspector)
   */
  private setupAudioV2Debug(): void {
    window.debugAudioV2 = () => {
      if (!this.audioEngine) {
        console.log('❌ No AudioEngineV2 initialized');
        return;
      }
      console.log('🔊 AudioEngineV2 Debug Info:');
      console.log('  State:', this.audioEngine.state);
      console.log('  Volume:', this.audioEngine.volume);
      console.log('  Sounds:', this.audioEngine.sounds);
      console.log('  Sound count:', this.audioEngine.sounds?.length || 0);

      if (this.audioEngine.sounds) {
        this.audioEngine.sounds.forEach((sound: AbstractSound, index: number) => {
          console.log(`  Sound ${index + 1}:`, {
            name: sound.name,
            volume: sound.volume,
            spatial: sound.spatial ? {
              enabled: true,
              maxDistance: sound.spatial.maxDistance,
              distanceModel: sound.spatial.distanceModel,
              rolloffFactor: sound.spatial.rolloffFactor
            } : 'Not spatial'
          });
        });
      }

      // Also show hotspot sounds
      const hotspotSounds = this.sceneManager?.getHotspotSounds();
      if (hotspotSounds) {
        console.log('  Hotspot sounds:', hotspotSounds);
      }
    };
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const loadingScreen = document.getElementById('loading');
    if (loadingScreen) {
      loadingScreen.innerHTML = `
        <div style="color: #ff6b6b; font-size: 18px;">
          <strong>Error</strong><br>
          ${message}
        </div>
      `;
      loadingScreen.classList.remove('hidden');
    }
  }


  /**
   * Get the scene manager (for debug utilities)
   */
  getSceneManager(): SceneManager {
    return this.sceneManager;
  }

  /**
   * Wait until async initialization completes.
   * Throws if initialization failed.
   */
  async waitUntilReady(): Promise<void> {
    await this.initializationPromise;
    if (this.initializationError) {
      throw this.initializationError;
    }
  }

  /**
   * Dispose and cleanup
   */
  dispose(): void {
    this.uiManager.dispose();
    this.sceneManager.dispose();
    this.xrHelper.dispose();
    this.scene.dispose();
    this.engine.dispose();
  }
}

// ============================================================================
// Exported Initialization Function
// ============================================================================

/**
 * Initialize the XRGallery viewer with the provided options
 *
 * @param options Initialization options
 * @returns Promise that resolves when viewer is initialized
 *
 * @example
 * // Hosted mode (with Firebase)
 * XRGallery.init({
 *   firebaseConfig: { apiKey: '...', ... },
 *   sequenceId: 'abc123'
 * });
 *
 * @example
 * // Export mode (standalone)
 * XRGallery.init({
 *   config: { experience: {...}, stages: [...] }
 * });
 */
export function init(options: InitOptions): Promise<WebXRGallery> {
  return new Promise((resolve) => {
    const createGallery = () => {
      const services = createServices(options);
      const canvas = setupCanvas(undefined);
      const gallery = new WebXRGallery(services, canvas);
      exposePortalStyleHelper(gallery);
      resolve(gallery);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', createGallery);
    } else {
      createGallery();
    }
  });
}

/**
 * Create an XRGallery viewer instance with a developer-friendly API.
 *
 * @example
 * // Simplest — load by scene ID from XRGallery cloud
 * const viewer = await XRGallery.create({ element: '#app', sceneId: 'abc123' });
 *
 * @example
 * // Full config — inline experience object
 * const viewer = await XRGallery.create({
 *   element: '#app',
 *   config: { experience: {...}, stages: [...] }
 * });
 *
 * @example
 * // React
 * const ref = useRef(null);
 * useEffect(() => {
 *   const v = XRGallery.create({ element: ref.current, sceneId });
 *   return () => v.then(v => v.dispose());
 * }, [sceneId]);
 * return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
 */
export async function create(options: CreateOptions): Promise<ViewerInstance> {
  const { initOptions, element } = resolveCreateOptions(options, DEFAULT_FIREBASE_CONFIG);

  // Wait for DOM if still loading
  await waitForDOMReady();

  // Set up the canvas inside the target element
  const canvas = setupCanvas(element);

  // Create services and viewer
  const services = createServices(initOptions);
  const galleryInstance = new WebXRGallery(services, canvas);
  exposePortalStyleHelper(galleryInstance);

  // Wait for async startup to succeed before returning
  try {
    await galleryInstance.waitUntilReady();
  } catch (error) {
    galleryInstance.dispose();
    throw error;
  }

  // Set page title if provided
  if (initOptions.title) {
    document.title = initOptions.title;
  }

  // Build ViewerInstance handle
  let disposed = false;
  const instance: ViewerInstance = {
    dispose() {
      if (!disposed) {
        disposed = true;
        galleryInstance.dispose();
      }
    },
    isDisposed() {
      return disposed;
    },
  };

  return instance;
}

// Expose on window for HTML script usage
if (typeof window !== 'undefined') {
  window.XRGallery = { init, create };
}

// Store reference to current gallery instance (exported for debugging)
export let gallery: WebXRGallery | null = null;

/**
 * Auto-initialize if running in legacy mode (viewer.html with embedded Firebase config)
 * This checks for window.__XRGALLERY_OPTIONS__ which viewer.html will set
 */
function autoInitialize() {
  // Check if options are provided via window global (set by viewer.html)
  const windowOptions = window.__XRGALLERY_OPTIONS__ as InitOptions | undefined;

  if (windowOptions) {
    logger.info('Auto-initializing with provided options');
    init(windowOptions).then((g) => {
      gallery = g;
    }).catch((error) => {
      logger.error('Failed to auto-initialize XRGallery:', error);
      Sentry.captureException(error);
      console.error('[XRGallery] Initialization failed:', error);
    });
  } else {
    // Check for embedded config (export mode)
    const embeddedConfig = window.__XRGALLERY_CONFIG__ as ExperienceConfig | undefined;
    if (embeddedConfig) {
      logger.info('Auto-initializing with embedded config (export mode)');
      init({ config: embeddedConfig }).then((g) => {
        gallery = g;
      }).catch((error) => {
        logger.error('Failed to auto-initialize XRGallery (export mode):', error);
        Sentry.captureException(error);
        console.error('[XRGallery] Export mode initialization failed:', error);
      });
    }
    // Otherwise, wait for explicit init() call from viewer.html
  }
}

// Run auto-initialization check
if (typeof window !== 'undefined') {
  // Initialize Web Vitals tracking (sends to Firebase Analytics)
  // Import analytics dynamically to avoid issues in export mode
  import('./firebase/config').then(({ analytics }) => {
    initWebVitals(analytics);
  }).catch(() => {
    // Export mode - no Firebase, just log vitals to console
    initWebVitals(null);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitialize);
  } else {
    autoInitialize();
  }
}

/**
 * Expose portal configuration helpers for easy testing
 */
function exposePortalStyleHelper(app: WebXRGallery) {
  const getPortalManager = () => app.getSceneManager().getPortalPreviewManager();

  // Set portal visual style
  window.setPortalStyle = (style: 'clean' | 'enhanced' | 'minimal' | 'alien' | 'floor-arrow') => {
    getPortalManager().setPortalEffectStyle(style);
    console.log(`✨ Portal effect style changed to: ${style}`);
    console.log('Available styles: clean, enhanced, minimal, alien, floor-arrow');
  };

  // Set portal rest size (size when not hovered)
  window.setPortalRestSize = (scale: number) => {
    getPortalManager().setPortalRestSize(scale);
    console.log(`📏 Portal rest size set to: ${scale}x (1.0 = default, 2.0 = 2x larger)`);
  };

  // Set portal hover size (size when hovered)
  window.setPortalHoverSize = (scale: number) => {
    getPortalManager().setPortalHoverSize(scale);
    console.log(`📏 Portal hover size set to: ${scale}x (1.0 = default, 2.0 = 2x larger)`);
  };

  // Get current portal configuration
  window.getPortalConfig = () => {
    const manager = getPortalManager();
    const sizes = manager.getPortalSizes();
    const style = manager.getPortalEffectStyle();
    console.log('Current portal configuration:');
    console.log(`  Style: ${style}`);
    console.log(`  Rest size: ${sizes.restSize}x`);
    console.log(`  Hover size: ${sizes.hoverSize}x`);
    return { style, ...sizes };
  };
}

// Optional: Enable inspector
// Press Ctrl+Shift+I to open the inspector
// scene.debugLayer.show();
